import { useCallback } from 'react';
import type { MutableRefObject, Ref } from 'react';
import { useTranslation } from 'react-i18next';

type UseInputTextParams = {
  disabled?: boolean;
  icon?: string;
  iconAriaLabel?: string;
  onClickIcon?: (...args: unknown[]) => void;
  onClickSecondaryIcon?: (...args: unknown[]) => void;
  ref?: Ref<HTMLInputElement>;
  referer?: Ref<HTMLInputElement>;
  secondaryIcon?: string;
  secondaryIconAriaLabel?: string;
};

// Forward a node to an external ref, supporting both callback and object refs. Whatever the callback returns is
// discarded: react-hook-form 6 passes `register` as `referer`, and on unmount `register(null)` returns a function,
// which React 18 warns about («A callback ref should not return a function») and React 19 would run as a cleanup.
function assignExternalRef(target: Ref<HTMLInputElement> | undefined, node: HTMLInputElement | null): void {
  if (!target) {
    return;
  }

  if (typeof target === 'function') {
    target(node);
  } else {
    (target as MutableRefObject<HTMLInputElement | null>).current = node;
  }
}

function useInputText({
  disabled,
  icon,
  iconAriaLabel,
  onClickIcon,
  onClickSecondaryIcon,
  ref,
  referer,
  secondaryIcon,
  secondaryIconAriaLabel,
}: UseInputTextParams) {
  const { t } = useTranslation();

  // Single callback ref that forwards the node to `referer` (react-hook-form) or, failing that, the forwarded `ref`.
  const refInput = useCallback(
    (node: HTMLInputElement | null) => {
      assignExternalRef(referer ?? ref, node);
    },
    [ref, referer]
  );

  const getIconAriaLabel = (iconType: string): string => {
    const iconLabels: Record<string, string> = {
      visible: t('buttons:Show password'),
      'visible-off': t('buttons:Hide password'),
      search: t('buttons:Search'),
      clear: t('buttons:Clear field'),
      close: t('buttons:Clear field'),
      calendar: t('buttons:Open calendar'),
      eye: t('buttons:Show password'),
      'eye-off': t('buttons:Hide password'),
    };

    return iconLabels[iconType] || t('buttons:Click {{icon}} icon', { icon: iconType });
  };

  const getIconAriaLabelValue = icon ? iconAriaLabel || getIconAriaLabel(icon) : undefined;

  const isIconClickable = typeof onClickIcon === 'function' && !disabled;

  const getSecondaryIconAriaLabelValue = secondaryIcon ? secondaryIconAriaLabel || getIconAriaLabel(secondaryIcon) : undefined;

  const isSecondaryIconClickable = typeof onClickSecondaryIcon === 'function' && !disabled;

  return {
    refInput,
    getIconAriaLabelValue,
    isIconClickable,
    getSecondaryIconAriaLabelValue,
    isSecondaryIconClickable,
  };
}

export default useInputText;
